import { useState } from 'react';
import { useLocation } from 'wouter';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { usePageTitle } from "@/hooks/usePageTitle";
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Lock, AlertTriangleIcon } from 'lucide-react';
import { getFirebaseAuth } from '@/lib/firebase';
import { updatePassword } from 'firebase/auth';

export default function PasswordReset() {
  usePageTitle("Password Reset Required");
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords match
    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please ensure both passwords are identical.",
        variant: "destructive",
      });
      return;
    }
    
    // Validate password strength
    if (newPassword.length < 8) {
      toast({
        title: "Password too weak",
        description: "Password must be at least 8 characters long.",
        variant: "destructive",
      });
      return;
    }
    
    setLoading(true);
    
    try {
      // Update password in Firebase
      const auth = await getFirebaseAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No authenticated user found");
      }
      
      await updatePassword(currentUser, newPassword);
      
      // Clear the requirePasswordReset flag in database
      await apiRequest(`/api/users/${user?.id}/clear-password-reset`, 'POST');
      
      // Refresh the auth context - this will trigger a re-render in App.tsx
      // which will switch to regular routes where /password-reset redirects to /dashboard
      await refreshUser();
      
      toast({
        title: "Password updated!",
        description: "Your password has been successfully changed.",
      });
    } catch (error: any) {
      console.error('Password reset error:', error);
      toast({
        title: "Password reset failed",
        description: error.message || "Failed to update password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle>Password Reset Required</CardTitle>
          <CardDescription>
            For your security, you must set a new password before continuing.
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <Alert className="mb-4">
            <AlertTriangleIcon className="h-4 w-4" />
            <AlertDescription>
              Your administrator has required you to change your password. Please create a strong, unique password.
            </AlertDescription>
          </Alert>
          
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="new-password"
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-8"
                  required
                  disabled={loading}
                  minLength={8}
                  data-testid="input-new-password"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Minimum 8 characters, use a mix of letters, numbers, and symbols
              </p>
            </div>
            
            <div>
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <div className="relative">
                <Lock className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-8"
                  required
                  disabled={loading}
                  minLength={8}
                  data-testid="input-confirm-password"
                />
              </div>
            </div>
            
            <Button
              type="submit"
              className="w-full"
              disabled={loading || !newPassword || !confirmPassword}
              data-testid="button-reset-password"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update Password
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}